import { useEffect, useState, useCallback, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
  Container, Box, Card, CardContent, CardActions,
  Button, Tooltip, Stack, IconButton,
  Dialog, DialogTitle, DialogContent, DialogActions,
  CircularProgress, Alert, Typography,
  useTheme, useMediaQuery,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ListIcon from '@mui/icons-material/List';

import { fetchPostView, fetchLikeStatus, fetchBookmarkStatus } from '../../api/posts';
import api from '../../api/axios';

import PostHeader from './PostHeader';
import PostContent from './PostContent';
import PostActions from './PostActions';
import VoteBox from './VoteBox';
import Comments from '../Comments';
import ReportModal from '../Reports/ReportModal';
import { useUser } from '../../contexts/UserContext';
import PinToggleButton from './PinToggleButton';

const BTN_W = 96; // PC 텍스트 버튼 최소 폭

export default function PostView() {
  /* ─ 기본 훅들 ─ */
  const { post_id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const backTarget = location.state?.from || `/main/board${location.search || ''}`;

  const { userInfo } = useUser();
  const isLoggedIn = !!userInfo;

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm')); // 600px 이하

  /* ─ 상태 ─ */
  const [post, setPost] = useState(null);
  const [isPinnedGlobal, setPinned] = useState(false);
  const [likeStatus, setLikeStatus] = useState({ liked: false, like_count: 0 });
  const [isBookmarked, setBookmarked] = useState(false);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState('');
  const [accessDenied, setDenied] = useState('');
  const [needConfirm, setConfirm] = useState(null);

  const reportModalRef = useRef(null);
  const openReport = (id, type) => reportModalRef.current?.open(id, type);

  /* ─ 데이터 로드 ─ */
  const loadPostData = useCallback(
    async (confirm = false) => {
      setLoading(true);
      try {
        const { data } = await fetchPostView(post_id, confirm);
        setPost(data);
        setPinned(data.is_pinned_global);
        setConfirm(null);

        const [likeRes, bookRes] = await Promise.all([
          fetchLikeStatus(post_id),
          fetchBookmarkStatus(post_id),
        ]);
        setLikeStatus(likeRes.data);
        setBookmarked(bookRes.data.bookmarked);
      } catch (err) {
        const { status, data = {} } = err.response || {};
        if (status === 402 && data.need_confirm) {
          // 차감 확인 모달
          setConfirm({ cost: data.cost, message: data.message });
        } else if ((status === 402 && data.error) || status === 403) {
          // 즉시 거부 (Leaf 부족 등)
          setDenied(data.error || data.message || 'Leaf🌿가 부족합니다.');
        } else {
          setFetchError('게시글을 불러오지 못했습니다.');
        }
      } finally {
        setLoading(false);
      }
    },
    [post_id]
  );

  useEffect(() => {
    loadPostData();
    window.scrollTo(0, 0);
  }, [loadPostData]);

  /* ─ 권한 ─ */
  const isAuthor = post && userInfo.username === (post.actual_author || '');
  const isAdmin = ['admin', 'master'].includes(userInfo?.role);

  /* ─ 가드 ─ */
  if (!isLoggedIn) return null;
  if (!post && !needConfirm && !accessDenied) return null;

  /* ─ 접근 불가 모달 ─ */
  if (accessDenied) {
    const isBlind = /블라인드/.test(accessDenied);
    const isSecret = /비밀글/.test(accessDenied);
    const isNoPoint = /Leaf|포인트/.test(accessDenied);

    return (
      <Dialog open onClose={() => navigate(backTarget)}>
        <DialogTitle>
          {isNoPoint
            ? '🌿 Leaf 부족'
            : isBlind
              ? '🔒 블라인드된 게시글입니다.'
              : isSecret
                ? '🔒 비밀글입니다.'
                : '⚠️ 접근 불가'}
        </DialogTitle>
        <DialogContent>{accessDenied}</DialogContent>
        <DialogActions>
          <Button onClick={() => navigate(backTarget)}>게시판으로 돌아가기</Button>
        </DialogActions>
      </Dialog>
    );
  }

  /* ─ 로딩 / 에러 / 삭제 처리 ─ */
  if (loading) {
    return (
      <Box sx={{ py: 6, textAlign: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (fetchError) {
    return (
      <Container maxWidth="sm" sx={{ py: 6 }}>
        <Alert severity="error">{fetchError}</Alert>
      </Container>
    );
  }

  if (post?.is_deleted && !isAdmin) {
    return (
      <Box sx={{ py: 6, textAlign: 'center' }}>
        <Typography variant="h5" sx={{ fontStyle: 'italic' }}>
          삭제된 게시글입니다.
        </Typography>
        <Button sx={{ mt: 3 }} onClick={() => navigate(backTarget)}>
          게시판으로 돌아가기
        </Button>
      </Box>
    );
  }

  /* ─ 화면 ─ */
  return (
    <>
      {/* 💰 Leaf 차감 확인 모달 */}
      <Dialog
        open={Boolean(needConfirm)}
        onClose={() => {
          setConfirm(null);
        }}
      >
        <DialogTitle>Leaf 차감 확인</DialogTitle>
        <DialogContent dividers>
          {needConfirm?.message ||
            `이 게시글은 ${needConfirm?.cost ?? 0} Leaf🌿가 사용됩니다.`}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirm(null)}>취소</Button>
          <Button
            variant="contained"
            onClick={() => {
              setConfirm(null);
              loadPostData(true); // ?confirm=1 재요청
            }}
          >
            {needConfirm?.cost ?? 0}🌿 사용
          </Button>
        </DialogActions>
      </Dialog>

      <Container maxWidth="md" sx={{ py: 4 }}>
        {post && (
          <Card>
            {/* ───── 글 본문 ───── */}
            <CardContent>
              <PostHeader post={post} />
              <PostContent post={post} />

              {post.has_vote && (
                <VoteBox
                  postId={post_id}
                  voteData={post}
                  hasVoted={post.user_has_voted}
                  isAnonymousVote={post.is_anonymous_vote}
                  allowMultiple={post.allow_multiple}
                  onVoteSuccess={loadPostData}
                  isAdmin={isAdmin}
                />
              )}

              {!post.is_deleted && (
                <PostActions
                  postId={post_id}
                  likeStatus={likeStatus}
                  setLikeStatus={setLikeStatus}
                  isBookmarked={isBookmarked}
                  setIsBookmarked={setBookmarked}
                  onReport={openReport}
                />
              )}
            </CardContent>

            {/* ───── 하단 액션 바 ───── */}
            <CardActions
              sx={{
                px: 2,
                py: 2,
                display: 'flex',
                alignItems: 'center',
                justifyContent: isAdmin ? 'space-between' : 'flex-end',
                flexWrap: { xs: 'wrap', sm: 'nowrap' },
                rowGap: 1,
              }}
            >
              {isAdmin && (
                <PinToggleButton
                  postId={post_id}
                  isPinned={post.is_pinned}
                  isPinnedGlobal={isPinnedGlobal}
                  onDone={loadPostData}
                  compact={!isMobile}
                />
              )}

              <Stack direction="row" spacing={1}>
                {!isMobile ? (
                  /* ─── PC: 텍스트 버튼 ─── */
                  <>
                    {isAuthor && !post.is_deleted && (
                      <Tooltip title="수정">
                        <Button
                          variant="outlined"
                          startIcon={<EditIcon />}
                          onClick={() =>
                            navigate(`/main/posts/${post_id}/edit${location.search}`, {
                              state: { from: backTarget },
                            })
                          }
                          sx={{ minWidth: BTN_W }}
                        >
                          수정
                        </Button>
                      </Tooltip>
                    )}

                    {(isAuthor || isAdmin) && !post.is_deleted && (
                      <Tooltip title="삭제">
                        <Button
                          variant="outlined"
                          color="error"
                          startIcon={<DeleteIcon />}
                          onClick={async () => {
                            if (window.confirm('정말 삭제할까요?')) {
                              await api.post(`/api/posts/${post_id}/soft-delete/`);
                              alert('삭제 완료');
                              navigate(backTarget);
                            }
                          }}
                          sx={{ minWidth: BTN_W }}
                        >
                          삭제
                        </Button>
                      </Tooltip>
                    )}

                    <Tooltip title="목록">
                      <Button
                        variant="text"
                        startIcon={<ListIcon />}
                        onClick={() => navigate(backTarget)}
                        sx={{ minWidth: BTN_W }}
                      >
                        목록
                      </Button>
                    </Tooltip>
                  </>
                ) : (
                  /* ─── 모바일: IconButton ─── */
                  <>
                    {isAuthor && !post.is_deleted && (
                      <Tooltip title="수정">
                        <IconButton
                          size="small"
                          sx={{ width: 24, height: 24, p: 0 }}
                          color="primary"
                          onClick={() =>
                            navigate(`/main/posts/${post_id}/edit${location.search}`, {
                              state: { from: backTarget },
                            })
                          }
                        >
                          <EditIcon sx={{ fontSize: 24 }} />
                        </IconButton>
                      </Tooltip>
                    )}

                    {(isAuthor || isAdmin) && !post.is_deleted && (
                      <Tooltip title="삭제">
                        <IconButton
                          size="small"
                          sx={{ width: 24, height: 24, p: 0 }}
                          color="error"
                          onClick={async () => {
                            if (window.confirm('정말 삭제할까요?')) {
                              await api.post(`/api/posts/${post_id}/soft-delete/`);
                              alert('삭제 완료');
                              navigate(backTarget);
                            }
                          }}
                        >
                          <DeleteIcon sx={{ fontSize: 24 }} />
                        </IconButton>
                      </Tooltip>
                    )}

                    <Tooltip title="목록">
                      <IconButton
                        size="small"
                        sx={{ width: 24, height: 24, p: 0 }}
                        onClick={() => navigate(backTarget)}
                      >
                        <ListIcon sx={{ fontSize: 24 }} />
                      </IconButton>
                    </Tooltip>
                  </>
                )}
              </Stack>
            </CardActions>
          </Card>
        )}

        {/* 댓글 */}
        {post && (!post.is_deleted || isAdmin) && (
          <Box sx={{ mt: 4 }}>
            <Comments
              postId={post_id}
              postAuthorId={post.author_id}              // ← 글쓴이의 user.id
              onReport={openReport}
            />
          </Box>
        )}

        <ReportModal ref={reportModalRef} />
      </Container>
    </>
  );
}
